import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { getCookingSessions, deleteCookingSession, resumeCookingSession } from '../services/api';
import menuSchedulingService from '../services/menuSchedulingService';
import CookNowModal from './CookNowModal';
import CookingCalendar from './CookingCalendar';
import { PageHeading, Heading, Text, Modal } from './ui';
import PixelArtLoader from './PixelArtLoader';
import QuestionMarkIcon from './QuestionMarkIcon';
import EducationModal from './EducationModal';
import FloatingCreateButton from './FloatingCreateButton';

const Activity = () => {
    const { user, userProfile } = useAuth();
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [scheduledMeals, setScheduledMeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all'); // 'all', 'completed', 'paused', 'in-progress'
    const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'

    // Modal state
    const [cookNowModalOpen, setCookNowModalOpen] = useState(false);
    const [selectedKernelId, setSelectedKernelId] = useState(null);
    
    // Education modal states
    const [showCalendarViewModal, setShowCalendarViewModal] = useState(false);
    const [showListViewModal, setShowListViewModal] = useState(false);
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [showDeleteSessionModal, setShowDeleteSessionModal] = useState(false);


    useEffect(() => {
        if (user) {
            fetchSessions();
        }
    }, [user]);

    const fetchSessions = async () => {
        try {
            setLoading(true);
            const token = await user.getIdToken();
            const data = await getCookingSessions(token);
            setSessions(data);
            
            // Also fetch scheduled meals for a wider date range (past 7 days to future 30 days)
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - 7); // Past 7 days
            const endDate = new Date(today);
            endDate.setDate(endDate.getDate() + 30); // Future 30 days
            
            try {
                const meals = await menuSchedulingService.getScheduledMeals(startDate, endDate, token);
                setScheduledMeals(meals);
            } catch (mealError) {
                console.error('Error fetching scheduled meals:', mealError);
                // Don't block if scheduled meals fail to load
            }
        } catch (err) {
            console.error('Error fetching sessions:', err);
            setError('Failed to load cooking sessions');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSession = async (sessionId) => {
        if (!window.confirm('Are you sure you want to delete this cooking session?')) {
            return;
        }
        
        try {
            const token = await user.getIdToken();
            await deleteCookingSession(sessionId, token);
            setSessions(sessions.filter(s => s.id !== sessionId));
        } catch (err) {
            console.error('Error deleting session:', err);
            alert('Failed to delete session. Please try again.');
        }
    };

    const handleDeleteScheduledMeal = async (mealId) => {
        if (!window.confirm('Are you sure you want to remove this scheduled meal?')) {
            return;
        }
        
        try {
            const token = await user.getIdToken();
            await menuSchedulingService.removeScheduledMeal(mealId, token);
            // Remove from state
            setScheduledMeals(scheduledMeals.filter(m => m.id !== mealId));
            // Refresh all data to update both list and calendar views
            fetchSessions();
        } catch (err) {
            console.error('Error deleting scheduled meal:', err);
            alert('Failed to remove scheduled meal. Please try again.');
        }
    };

    const handleResumeSession = async (session) => {
        try {
            const token = await user.getIdToken();
            await resumeCookingSession(session.id, token);
            
            // Navigate back to the live session - handle both draft and recipe sessions
            if (session.recipe_id) {
                navigate(`/live/recipe/${session.recipe_id}`);
            } else if (session.draft_id) {
                navigate(`/live/${session.draft_id}`);
            } else {
                console.error('No valid session ID found for navigation');
                alert('Unable to resume session - invalid session data.');
            }
        } catch (err) {
            console.error('Error resuming session:', err);
            alert('Failed to resume session. Please try again.');
        }
    };

    const handleContinueSession = async (session) => {
        try {
            // Navigate directly to the live session - handle both draft and recipe sessions
            if (session.recipe_id) {
                navigate(`/live/recipe/${session.recipe_id}`);
            } else if (session.draft_id) {
                navigate(`/live/${session.draft_id}`);
            } else {
                console.error('No valid session ID found for navigation');
                alert('Unable to continue session - invalid session data.');
            }
        } catch (err) {
            console.error('Error continuing session:', err);
            alert('Failed to continue session. Please try again.');
        }
    };

    const handleRecreateRecipe = async (session) => {
        // ... existing code ...
    };

    const formatDuration = (minutes) => {
        if (!minutes) return 'N/A';
        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hrs > 0) {
            return `${hrs}h ${mins}m`;
        }
        return `${mins}m`;
    };

    const formatActualDuration = (seconds) => {
        if (!seconds) return null;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (mins > 0) {
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        }
        return `0:${secs.toString().padStart(2, '0')}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Handle Cook Now button click
    const handleCookNow = (kernelId) => {
        setSelectedKernelId(kernelId);
        setCookNowModalOpen(true);
    };

    // Handle successful cook session
    const handleCookSuccess = (draftId) => {
        setCookNowModalOpen(false);
        setSelectedKernelId(null);
        navigate(`/live/${draftId}`);
    };



    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <PixelArtLoader 
                            message="LOADING COOKING ACTIVITY"
                            subMessage="Fetching your culinary sessions..."
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Heading level={2}>Error</Heading>
                        <Text>{error}</Text>
                    </div>
                </div>
            </div>
        );
    }

    // Color tokens for consistent styling
    const colorTokens = {
        oxfordBlue: '#1b263b',
        alabaster: '#EEEEE7',
        white: '#FFFFFF',
        richBlack: '#0d1b2a'
    };

    return (
        <>
        {/* Floating Create Button */}
        {user && <FloatingCreateButton />}
        
        <div className="min-h-screen bg-neutral-50">
            <div className="w-full px-0 py-8">
                <div className="max-w-6xl mx-auto mb-6">
                    <div className="flex items-center justify-between">
                        <Heading level={1} className="text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>COOKING ACTIVITY</Heading>
                        
                        {/* View Toggle */}
                        <div className="flex items-center space-x-1">
                            <div className="relative group">
                                <button
                                    onClick={() => setViewMode('calendar')}
                                    className={`w-8 h-8 flex items-center justify-center transition-colors ${
                                        viewMode === 'calendar' 
                                            ? 'bg-neutral-900 text-neutral-0' 
                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100'
                                    }`}
                                    title="Calendar View"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                        <line x1="16" y1="2" x2="16" y2="6"/>
                                        <line x1="8" y1="2" x2="8" y2="6"/>
                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                    </svg>
                                </button>
                                <QuestionMarkIcon 
                                    onOpenModal={() => setShowCalendarViewModal(true)} 
                                    onCloseModal={() => setShowCalendarViewModal(false)}
                                    isModalOpen={showCalendarViewModal}
                                />
                            </div>
                            <div className="relative group">
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`w-8 h-8 flex items-center justify-center transition-colors ${
                                        viewMode === 'list' 
                                            ? 'bg-neutral-900 text-neutral-0' 
                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100'
                                    }`}
                                    title="List View"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="8" y1="6" x2="21" y2="6"/>
                                        <line x1="8" y1="12" x2="21" y2="12"/>
                                        <line x1="8" y1="18" x2="21" y2="18"/>
                                        <line x1="3" y1="6" x2="3.01" y2="6"/>
                                        <line x1="3" y1="12" x2="3.01" y2="12"/>
                                        <line x1="3" y1="18" x2="3.01" y2="18"/>
                                    </svg>
                                </button>
                                <QuestionMarkIcon 
                                    onOpenModal={() => setShowListViewModal(true)} 
                                    onCloseModal={() => setShowListViewModal(false)}
                                    isModalOpen={showListViewModal}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Calendar View */}
                {viewMode === 'calendar' && (
                    <CookingCalendar
                        isOpen={true}
                        onClose={() => {}}
                        sessions={sessions}
                        scheduledMeals={scheduledMeals}
                        onSessionClick={handleResumeSession}
                        onSessionDelete={handleDeleteSession}
                        onScheduledMealDelete={handleDeleteScheduledMeal}
                    />
                )}

                {/* List View */}
                {viewMode === 'list' && (
                    <div className="max-w-6xl mx-auto">
                        {/* Main Content */}
                        <div className="bg-neutral-0 hairline">
                            <div className="flex-1 overflow-y-auto no-scrollbar">
                                    {sessions.length === 0 ? (
                                        <div className="text-center py-12 minimal-padding">
                                            <Text className="text-lg mb-4">No cooking sessions yet.</Text>
                                            <Text className="mb-6">Start cooking to see your activity here!</Text>
                                            <Link 
                                                to="/bases" 
                                                className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                            >
                                                BROWSE TECHNIQUES
                                            </Link>
                                        </div>
                                    ) : (
                                        <div>
                                            {/* Column Headers - Hidden on smallest screens */}
                                            <div className="hidden sm:grid grid-cols-5 gap-4 mb-4 minimal-padding" style={{gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr'}}>
                                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                                    TECHNIQUE
                                                </div>
                                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                                    DURATION
                                                </div>
                                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                                    STATUS
                                                </div>
                                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                                    DATE
                                                </div>
                                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                                    ACTIONS
                                                </div>
                                            </div>
                                            
                                            {/* Divider line between headers and sessions */}
                                            <div className="border-t border-neutral-900 mb-2"></div>

                                            {/* Scheduled Meals */}
                                            {scheduledMeals.length > 0 && (
                                                <>
                                                    <div className="minimal-padding py-2 border-b border-neutral-900">
                                                        <Text className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                                            SCHEDULED MEALS
                                                        </Text>
                                                    </div>
                                                    {scheduledMeals.map((meal, index) => {
                                                        const mealDate = new Date(meal.scheduled_date);
                                                        const today = new Date();
                                                        today.setHours(0, 0, 0, 0);
                                                        mealDate.setHours(0, 0, 0, 0);
                                                        const isToday = mealDate.getTime() === today.getTime();
                                                        const isPast = mealDate.getTime() < today.getTime();
                                                        
                                                        return (
                                                            <div 
                                                                key={meal.id}
                                                                className={`py-2 minimal-padding hover:bg-neutral-50 transition-colors duration-120 ${index > 0 ? 'border-t border-neutral-200' : ''}`}
                                                            >
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center gap-3">
                                                                        <span className={`font-mono text-xs uppercase tracking-wider px-2 py-1 ${
                                                                            isToday 
                                                                                ? 'bg-[#1b263b] text-white' 
                                                                                : isPast
                                                                                ? 'bg-neutral-900 text-neutral-0 opacity-60'
                                                                                : 'bg-[#1b263b] text-white opacity-80'
                                                                        }`}>
                                                                            {isToday ? 'TODAY' : isPast ? 'PAST' : mealDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                                        </span>
                                                                        <Text className="text-neutral-900 font-medium">
                                                                            {meal.recipe_name}
                                                                        </Text>
                                                                        {meal.meal_type && (
                                                                            <Text className="font-mono text-xs text-neutral-900 opacity-60 capitalize">
                                                                                {meal.meal_type}
                                                                            </Text>
                                                                        )}
                                                                        {meal.cooking_time && (
                                                                            <Text className="font-mono text-xs text-neutral-900 opacity-60">
                                                                                {meal.cooking_time}m
                                                                            </Text>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            onClick={() => navigate(`/recipes/${meal.recipe_id}`)}
                                                                            className="px-3 py-1 bg-[#1b263b] text-white hover:opacity-60 transition-opacity font-mono text-xs uppercase tracking-wider border-none"
                                                                        >
                                                                            START COOKING
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleDeleteScheduledMeal(meal.id)}
                                                                            className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity px-2 py-1"
                                                                            title="Delete scheduled meal"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {sessions.length > 0 && (
                                                        <div className="minimal-padding py-2 border-t border-neutral-900 mt-2">
                                                            <Text className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                                                COOKING SESSIONS
                                                            </Text>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Session Rows */}
                                            <div>
                                                {sessions
                                                    .sort((a, b) => {
                                                        // Sort by priority: paused sessions first, then by creation date
                                                        if (a.is_paused && !b.is_paused) return -1;
                                                        if (!a.is_paused && b.is_paused) return 1;
                                                        return new Date(b.created_at) - new Date(a.created_at);
                                                    })
                                                    .map((session, index) => (
                                                    <div 
                                                        key={session.id} 
                                                        className={`py-2 minimal-padding hover:bg-neutral-50 transition-colors duration-120 cursor-pointer ${index > 0 ? 'border-t border-neutral-200' : ''}`}
                                                    >
                                                        {/* Mobile Layout */}
                                                        <div className="sm:hidden space-y-2">
                                                            {/* Top Row: Technique Name and Status */}
                                                            <div className="space-y-1">
                                                                <Text className="text-neutral-900 font-medium text-sm leading-tight">
                                                                    {session.recipe_name || session.kernel_title || 'Custom Recipe'}
                                                                </Text>
                                                                <span className={`px-2 py-1 text-xs font-mono uppercase tracking-wider inline-block ${
                                                                    session.was_completed 
                                                                        ? 'bg-neutral-900 text-neutral-0' 
                                                                        : session.is_paused
                                                                        ? 'bg-yellow-600 text-neutral-0'
                                                                        : 'bg-neutral-50 text-neutral-900 hairline'
                                                                }`}>
                                                                    {session.was_completed ? 'DONE' : session.is_paused ? 'PAUSED' : 'IN PROGRESS'}
                                                                </span>
                                                            </div>
                                                            
                                                            {/* Bottom Row: Duration, Date, Actions */}
                                                            <div className="flex justify-between items-center">
                                                                <div className="flex items-center space-x-3">
                                                                    <Text className="font-mono text-xs text-neutral-900">
                                                                        {session.actual_duration_seconds ? 
                                                                            formatActualDuration(session.actual_duration_seconds) : 
                                                                            formatDuration(session.total_duration_minutes)
                                                                        }
                                                                        {session.actual_duration_seconds && (
                                                                            <span className="text-xs text-neutral-900 opacity-60 ml-1">
                                                                                actual
                                                                            </span>
                                                                        )}
                                                                    </Text>
                                                                    <Text className="font-mono text-xs text-neutral-900 opacity-60">
                                                                        {new Date(session.created_at).toLocaleDateString()}
                                                                    </Text>
                                                                </div>
                                                                
                                                                <div className="flex space-x-1">
                                                                    {session.is_paused ? (
                                                                        <button 
                                                                            onClick={() => handleResumeSession(session)}
                                                                            className="text-xs font-mono uppercase tracking-wider bg-green-600 text-neutral-0 px-2 py-1 hover:bg-green-700 transition-colors"
                                                                        >
                                                                            ▶
                                                                        </button>
                                                                    ) : session.was_completed ? (
                                                                        <span className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 px-2 py-1">
                                                                            DONE
                                                                        </span>
                                                                    ) : (
                                                                        <button 
                                                                            onClick={() => handleContinueSession(session)}
                                                                            className="text-xs font-mono uppercase tracking-wider bg-neutral-900 text-neutral-0 px-2 py-1 hover:opacity-60 transition-opacity"
                                                                        >
                                                                            ▶
                                                                        </button>
                                                                    )}
                                                                    
                                                                    <button 
                                                                        onClick={() => handleDeleteSession(session.id)}
                                                                        className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity px-2 py-1"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Desktop Layout */}
                                                        <div className="hidden sm:grid grid-cols-5 gap-3 py-1" style={{gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr'}}>
                                                            <div className="flex items-center min-w-0">
                                                                <Text className="text-neutral-900 truncate font-medium">
                                                                    {session.recipe_name || session.kernel_title || 'Custom Recipe'}
                                                                </Text>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <Text className="font-mono text-sm text-neutral-900">
                                                                    {session.actual_duration_seconds ? 
                                                                        formatActualDuration(session.actual_duration_seconds) : 
                                                                        formatDuration(session.total_duration_minutes)
                                                                    }
                                                                </Text>
                                                                {session.actual_duration_seconds && (
                                                                    <Text className="text-xs text-neutral-900 opacity-60 ml-1">
                                                                        actual
                                                                    </Text>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center">
                                                                <span className={`px-2 py-1 text-xs font-mono uppercase tracking-wider ${
                                                                    session.was_completed 
                                                                        ? 'bg-neutral-900 text-neutral-0' 
                                                                        : session.is_paused
                                                                        ? 'bg-yellow-600 text-neutral-0'
                                                                        : 'bg-neutral-50 text-neutral-900 hairline'
                                                                }`}>
                                                                    {session.was_completed ? 'DONE' : session.is_paused ? 'PAUSED' : 'IN PROGRESS'}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <span className="font-mono text-sm text-neutral-900 opacity-60">
                                                                    {new Date(session.created_at).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <div className="flex space-x-2">
                                                                    {session.is_paused ? (
                                                                        <button 
                                                                            onClick={() => handleResumeSession(session)}
                                                                            className="text-xs font-mono uppercase tracking-wider bg-green-600 text-neutral-0 px-3 py-1 hover:bg-green-700 transition-colors"
                                                                        >
                                                                            ▶ RESUME
                                                                        </button>
                                                                    ) : session.was_completed ? (
                                                                        <span className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 px-3 py-1">
                                                                            COMPLETED
                                                                        </span>
                                                                    ) : (
                                                                        <button 
                                                                            onClick={() => handleContinueSession(session)}
                                                                            className="text-xs font-mono uppercase tracking-wider bg-neutral-900 text-neutral-0 px-3 py-1 hover:opacity-60 transition-opacity"
                                                                        >
                                                                            CONTINUE
                                                                        </button>
                                                                    )}
                                                                    
                                                                    <button 
                                                                        onClick={() => handleDeleteSession(session.id)}
                                                                        className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity px-2 py-1"
                                                                    >
                                                                        DEL
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Cook Now Modal */}
            <CookNowModal
                kernelId={selectedKernelId}
                isOpen={cookNowModalOpen}
                onClose={() => {
                    setCookNowModalOpen(false);
                    setSelectedKernelId(null);
                }}
                onSuccess={handleCookSuccess}
            />

        </div>
        <>
            {/* Education Modals */}
            <EducationModal
                isOpen={showCalendarViewModal}
                onClose={() => setShowCalendarViewModal(false)}
                title="Calendar View"
                description="View your cooking sessions in a calendar format. See when you cooked each recipe and navigate through your cooking history by date."
                image="/images/education/calendar-view-demo.png"
                details={[
                    "View sessions organized by date on a calendar",
                    "See all your cooking activity at a glance",
                    "Navigate through months to find past sessions",
                    "Click on sessions to view details or resume"
                ]}
            />
            <EducationModal
                isOpen={showListViewModal}
                onClose={() => setShowListViewModal(false)}
                title="List View"
                description="View your cooking sessions in a detailed list format. See all session information including duration, status, and date in one place."
                image="/images/education/list-view-demo.png"
                details={[
                    "View all sessions in a detailed list",
                    "See session duration, status, and date",
                    "Resume paused sessions or continue active ones",
                    "Delete sessions you no longer need"
                ]}
            />
            <EducationModal
                isOpen={showResumeModal}
                onClose={() => setShowResumeModal(false)}
                title="Resume Session"
                description="Continue a paused cooking session. Pick up where you left off with all your timers and progress saved."
                image="/images/education/resume-demo.png"
                details={[
                    "Resume paused cooking sessions",
                    "Continue from where you left off",
                    "All timers and progress are preserved",
                    "Navigate back to the live cooking interface"
                ]}
            />
            <EducationModal
                isOpen={showDeleteSessionModal}
                onClose={() => setShowDeleteSessionModal(false)}
                title="Delete Session"
                description="Remove a cooking session from your activity history. This action cannot be undone."
                image="/images/education/delete-session-demo.png"
                details={[
                    "Permanently remove sessions from your history",
                    "Cannot be undone once deleted",
                    "Session data and reflections will be removed",
                    "Use this to clean up old or unwanted sessions"
                ]}
            />
        </>
        </>
    );
};

export default Activity; 
